/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect } from "react";
import eventBus from "../../components/eventBus";
import { useAuth } from "./../../AuthContext.js";
import Logger from "../../utils/Logger";
import httpClient from "../../services/httpClient.js";
import ReservationTable from "../../components/ReservationTable";
import { useTranslation } from "react-i18next";

const ConferenceRooms = () => {
  const { t } = useTranslation();
  const [date, setDate] = useState(new Date());
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [transformedReservations, setTransformedReservations] = useState([]);
  const { userData } = useAuth();
  const [onlyMine, setOnlyMine] = useState(false); // Stan dla filtracji tylko moich rezerwacji
  const [rooms, setRooms] = useState([]); // Sale pobrane z backendu
  const [rawReservations, setRawReservations] = useState([]); // Surowe dane rezerwacji

  // Mapa kolorów: używamy colorHex z backendu
  const roomColorMap = React.useMemo(() => {
    const colorMap = {};
    rooms.forEach((room) => {
      colorMap[room.name] = room.colorHex || "#666";
    });
    return colorMap;
  }, [rooms]);

  // Function to expand recurring reservations to specific dates for the month
  const expandRecurringReservations = (recurringReservations, month, year) => {
    const expandedReservations = [];

    if (!Array.isArray(recurringReservations)) {
      Logger.warn(
        "expandRecurringReservations: recurringReservations is not an array",
        recurringReservations,
      );
      return [];
    }

    // Create a mapping from room ID to room name based on known rooms
    const roomIdToName = {
      1: "Sala mała",
      2: "Sala duża",
      3: "Sala czerwona",
      4: "Sala niviss",
    };

    recurringReservations.forEach((recurringReservation) => {
      try {
        // Validate required fields
        if (
          !recurringReservation.dayOfWeek ||
          !recurringReservation.fromTime ||
          !recurringReservation.toTime
        ) {
          Logger.warn(
            "expandRecurringReservations: Missing required fields",
            recurringReservation,
          );
          return;
        }

        // Get all dates for the specified month that match the dayOfWeek
        const datesInMonth = getDatesForDayOfWeek(
          recurringReservation.dayOfWeek,
          month,
          year,
        );

        // Determine room name
        let conferenceRoomName = recurringReservation.conferenceRoomName;
        if (!conferenceRoomName && recurringReservation.conferenceRoomId) {
          conferenceRoomName =
            roomIdToName[recurringReservation.conferenceRoomId] ||
            `Sala ${recurringReservation.conferenceRoomId}`;
        }
        if (!conferenceRoomName) {
          conferenceRoomName = t("reservations.rooms.unknownRoom");
        }

        datesInMonth.forEach((date) => {
          // Create a regular reservation object for each date
          expandedReservations.push({
            id: recurringReservation.id, // Keep original ID for proper backend communication
            originalRecurringId: recurringReservation.id, // Keep reference to original recurring reservation
            expandedId: `recurring_${recurringReservation.id}_${
              date.toISOString().split("T")[0]
            }`, // Unique ID for frontend display purposes
            reservationDate: date.toISOString().split("T")[0], // Format as YYYY-MM-DD
            fromTime: recurringReservation.fromTime,
            toTime: recurringReservation.toTime,
            title: recurringReservation.title || "Cykliczne spotkanie",
            organizer: recurringReservation.organizer || "Nieznany organizator",
            emailAddress: recurringReservation.emailAddress || "",
            conferenceRoomName: conferenceRoomName,
            conferenceRoomId: recurringReservation.conferenceRoomId,
            attendees: recurringReservation.attendees || {},
            isRecurring: true, // Mark as expanded from recurring
            dayOfWeek: recurringReservation.dayOfWeek, // Keep day of week for recurring logic
          });
        });
      } catch (error) {
        Logger.error(
          "Error processing recurring reservation:",
          error,
          recurringReservation,
        );
      }
    });

    Logger.log(
      `Expanded ${recurringReservations.length} recurring reservations to ${expandedReservations.length} specific dates`,
    );
    return expandedReservations;
  };

  // Helper function to get all dates in a month that match a specific day of week
  const getDatesForDayOfWeek = (dayOfWeek, month, year) => {
    const dates = [];
    const daysInMonth = new Date(year, month, 0).getDate(); // Get number of days in month

    for (let day = 1; day <= daysInMonth; day++) {
      const date = new Date(year, month - 1, day); // month - 1 because Date constructor expects 0-based month

      // dayOfWeek: 1 = Monday, 2 = Tuesday, ..., 7 = Sunday
      // date.getDay(): 0 = Sunday, 1 = Monday, ..., 6 = Saturday
      let dateDay = date.getDay();
      if (dateDay === 0) dateDay = 7; // Convert Sunday from 0 to 7

      if (dateDay === dayOfWeek) {
        dates.push(date);
      }
    }

    return dates;
  };

  const fetchMonthAndYearData = async (newDate, mine = false) => {
    const newMonth = newDate.getMonth() + 1; // getMonth() returns 0-11
    const newYear = newDate.getFullYear();

    setDate(newDate);

    Logger.log(
      `Fetching data for month: ${newMonth}, year: ${newYear}, mine: ${mine}`,
    );
    // setLoading(true);
    setError(null);

    try {
      // ZAWSZE pobieramy wszystkie rezerwacje
      // Filtrowanie "tylko moje" będzie robione w frontendzie na podstawie isUserInvolved
      const url = `/RoomReservations/month?year=${newYear}&month=${newMonth}`;

      const [regularResponse, recurringResponse] = await Promise.all([
        httpClient.get(url, { withAuth: true }),
        // httpClient
        //   .get(`/conferences/RecurringReservations`, { withAuth: true })
        //   .catch((error) => {
        //     // If recurring endpoint fails, just log and continue with empty array
        //     Logger.warn("Nie udało się pobrać cyklicznych rezerwacji:", error);
        //     return { data: [] };
        //   }),
        [],
      ]);

      const regularReservations = regularResponse.data || [];
      const recurringReservations = recurringResponse.data || [];

      Logger.log("Regular reservations:", regularReservations);
      Logger.log("Recurring reservations:", recurringReservations);

      // Expand recurring reservations to specific dates
      const expandedRecurringReservations = expandRecurringReservations(
        recurringReservations,
        newMonth,
        newYear,
      );

      Logger.log(
        "Expanded recurring reservations:",
        expandedRecurringReservations,
      );

      // Combine both types of reservations
      const allReservations = [
        ...regularReservations,
        ...expandedRecurringReservations,
      ];

      setRawReservations(allReservations);
    } catch (error) {
      setError(error.message || t("reservations.common.fetchDataError"));
      Logger.error("Błąd podczas pobierania danych:", error);
    } finally {
      setLoading(false);
    }
  };

  const mapToCalendarEvents = React.useCallback(
    (rawReservations) => {
      return rawReservations.map((reservation) => {
        let start,
          end,
          reservationDate,
          fromTime,
          toTime,
          conferenceRoomName,
          organizer;

        if (reservation.isRecurring) {
          // For expanded recurring reservations, use the generated date
          start = new Date(
            `${reservation.reservationDate}T${reservation.fromTime}`,
          );
          end = new Date(
            `${reservation.reservationDate}T${reservation.toTime}`,
          );
          reservationDate = reservation.reservationDate;
          fromTime = reservation.fromTime;
          toTime = reservation.toTime;
          conferenceRoomName = reservation.conferenceRoomName;
          organizer = reservation.organizer;
        } else {
          // For regular reservations, parse ISO datetime strings
          start = new Date(reservation.startTime);
          end = new Date(reservation.endTime);

          // Extract date and time components for compatibility
          reservationDate = start.toISOString().split("T")[0]; // YYYY-MM-DD
          fromTime = start.toTimeString().slice(0, 5); // HH:mm
          toTime = end.toTimeString().slice(0, 5); // HH:mm
          conferenceRoomName = reservation.room;
          organizer = reservation.createdBy;
        }

        // Check if current user is involved in this reservation
        const isCreator = organizer === userData?.userName;

        // Check invitations array for attendees
        // Backend może zwracać różne formaty - sprawdź wszystkie możliwe pola
        const isAttendee =
          Array.isArray(reservation.invitations) &&
          reservation.invitations.some(
            (inv) =>
              inv.userEmail === userData?.userEmail ||
              inv.userName === userData?.userName ||
              inv.userFullName === userData?.userName,
          );

        const isUserInvolved = isCreator || isAttendee;

        const title = reservation.isRecurring
          ? `🔄 ${organizer} - ${reservation.title}`
          : `${organizer} - ${reservation.title}`;

        const color = roomColorMap[conferenceRoomName] || "#666";
        return {
          title,
          start,
          end,
          allDay: false,
          resource: {
            ...reservation,
            // Add normalized fields for compatibility
            reservationDate,
            fromTime,
            toTime,
            conferenceRoomName,
            organizer,
            color,
            type: "conferenceRoom",
            isUserInvolved,
            recurring: reservation.isRecurring || false, // Mark if this is from a recurring reservation
          },
        };
      });
    },
    [roomColorMap, userData],
  );

  // Pobierz listę sal z backendu
  useEffect(() => {
    const fetchRooms = async () => {
      try {
        const response = await httpClient.get("/Rooms", { withAuth: true });
        setRooms(response.data || []);
        Logger.log("Pobrano sale z backendu:", response.data);
      } catch (error) {
        Logger.error("Błąd podczas pobierania sal:", error);
        setRooms([]);
      }
    };

    fetchRooms();
  }, []);

  // Przelicz transformedReservations gdy zmienią się rawReservations lub roomColorMap
  useEffect(() => {
    if (rawReservations.length > 0) {
      Logger.log("Przeliczam transformedReservations z nowymi kolorami");
      setTransformedReservations(mapToCalendarEvents(rawReservations));
    }
  }, [rawReservations, mapToCalendarEvents]);

  useEffect(() => {
    Logger.log("Fetching initial data for current date:", date);
    fetchMonthAndYearData(new Date());
  }, []);

  useEffect(() => {
    Logger.log("onlyMine state changed:", onlyMine);
    fetchMonthAndYearData(date, onlyMine);
  }, [onlyMine]);

  // Event bus subscription
  useEffect(() => {
    const handleConferenceRoomReservationDeleted = () => {
      fetchMonthAndYearData(date);
    };

    const handleReFetchRoomReservations = () => {
      fetchMonthAndYearData(date);
    };

    const handleRoomReserved = () => {
      fetchMonthAndYearData(date);
    };

    const handleRecurringReservationCreated = () => {
      fetchMonthAndYearData(date);
    };

    eventBus.on(
      "room-reservation-deleted",
      handleConferenceRoomReservationDeleted,
    );

    eventBus.on("reFetchRoomReservations", handleReFetchRoomReservations);
    eventBus.on("room-reserved", handleRoomReserved);
    eventBus.on(
      "recurring-reservation-created",
      handleRecurringReservationCreated,
    );

    return () => {
      eventBus.off(
        "room-reservation-deleted",
        handleConferenceRoomReservationDeleted,
      );
      eventBus.off("reFetchRoomReservations", handleReFetchRoomReservations);
      eventBus.off("room-reserved", handleRoomReserved);
      eventBus.off(
        "recurring-reservation-created",
        handleRecurringReservationCreated,
      );
    };
  }, [fetchMonthAndYearData, date]); // Added date dependency since we use it in the handlers

  // Filtrowanie danych
  const filteredData = React.useMemo(() => {
    let data = transformedReservations;

    // Filtracja tylko moich rezerwacji
    if (onlyMine && userData?.userName) {
      data = data.filter((item) => {
        return item.resource?.isUserInvolved === true;
      });
    }

    return data;
  }, [transformedReservations, onlyMine, userData]);

  // Create legend items for conference rooms (dynamicznie z backendu)
  const legendItems = React.useMemo(() => {
    return rooms.map((room) => ({
      key: room.id,
      label: room.name,
      color: roomColorMap[room.name] || "#666",
    }));
  }, [rooms, roomColorMap]);

  // Przycisk rezerwacji
  const button = {
    createLabel: t("reservations.rooms.makeReservation"),
    createLink: "/rezerwacje/sale-konferencyjne/zarezerwuj",
  };

  // Przycisk zarządzania dla administratora
  const adminButton =
    userData.userRole === "admin"
      ? {
          link: "/administrator/sale-konferencyjne",
          label: t("reservations.rooms.manageRooms"),
        }
      : null;

  return (
    <>
      <ReservationTable
        title={t("reservations.rooms.title")}
        onMonthAndYearChange={(newDate) =>
          fetchMonthAndYearData(newDate, onlyMine)
        }
        data={filteredData}
        loading={loading}
        error={error}
        button={button}
        adminButton={adminButton}
        legendItems={legendItems}
        legendTitle={t("reservations.rooms.legendTitle")}
        showOnlyMine={onlyMine}
        onToggleOnlyMine={() => setOnlyMine(!onlyMine)}
        currentDate={date}
      />
    </>
  );
};

export default ConferenceRooms;
